import React, { FC } from "react";
import { Button, Divider, Modal, Select, Spin, Typography } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import styles from "./modal.css";
import {
  Proposal,
  MSWordFont,
  Brand,
  PricingLevel,
  formatPrice,
  NumberedListFormat,
} from "~/design-system";
import { TaxLabel } from "~/gql/graphql";
import {
  ProposalExportPreviewModalBody,
  SpacerRow,
  Row,
  formatPercentage,
} from "~/design-system/components/proposal-export/modal-body";
import { TotalStrings } from "~/design-system/components/matter-editor/pricing-text-creators";
import { useNavigate } from "react-router";

const {
  ["body-container"]: bodyContainerClassName,
  ["proposal-customisation-selectors"]: proposalCustomisationSelectors,
} = styles as any;

const { Text } = Typography;

export type ProposalExportPreviewModalProps = {
  show: boolean;
  loading: boolean;
  logo: string;
  footer: string;
  downloading: boolean;
  proposals?: Proposal[];
  onClose: () => void;
  onCancel: () => void;
  onDownload: () => void;
  taxLevelPercent: number;
  fontFamily: MSWordFont;
  brands: Brand[];
  selectedBrand: number;
  selectBrand: (brandId: number) => Promise<void>;
  includeTasksInProposalOutput: boolean;
  includeRatesInProposalOutput: boolean;
  includeFeeEarnersInProposalOutput: boolean;
  setIncludeRatesInProposalOutputAtMatterLevel: (include: boolean) => Promise<void>;
  setIncludeTasksInProposalOutputAtMatterLevel: (include: boolean) => Promise<void>;
  setIncludeFeeEarnersInProposalOutputAtMatterLevel: (include: boolean) => Promise<void>;
  currencySymbol: string;
  taxLabel: TaxLabel;
  disableTaskToggle?: boolean;
  excludeTax: boolean;
  totals: TotalStrings<string>;
  taxPercent: number;
  collated?: Proposal;
  numberedListFormat: NumberedListFormat;
  lineBreakInLists: boolean;
};

const ProposalPreviewTitle: FC<{
  brands: Brand[];
  selectedBrand: number;
  selectBrand: (brandId: number) => Promise<void>;
  includeTasksInProposalOutput: boolean;
  includeRatesInProposalOutput: boolean;
  includeFeeEarnersInProposalOutput: boolean;
  setIncludeRatesInProposalOutputAtMatterLevel: (include: boolean) => Promise<void>;
  setIncludeTasksInProposalOutputAtMatterLevel: (include: boolean) => Promise<void>;
  setIncludeFeeEarnersInProposalOutputAtMatterLevel: (include: boolean) => Promise<void>;
  disableToggle?: boolean;
  showHideRates: boolean;
}> = ({
  brands,
  selectedBrand,
  selectBrand,
  includeTasksInProposalOutput,
  includeRatesInProposalOutput,
  includeFeeEarnersInProposalOutput,
  setIncludeTasksInProposalOutputAtMatterLevel,
  setIncludeRatesInProposalOutputAtMatterLevel,
  setIncludeFeeEarnersInProposalOutputAtMatterLevel,
  disableToggle,
  showHideRates,
}) => {
  return (
    <div className={styles["header-container"]}>
      <span>Proposal Preview</span>
      {brands.length > 0 && (
        <>
          <span className={proposalCustomisationSelectors}>Brand:</span>
          <Select
            options={brands.map((b) => {
              return {
                value: b.id,
                label: b.organisationName,
              };
            })}
            placeholder="Select Proposal Brand"
            onSelect={(_, option) => selectBrand(option.value)}
            defaultValue={selectedBrand || brands.find((b) => b.isDefault)?.id || undefined}
          />
        </>
      )}
      <>
        <span className={proposalCustomisationSelectors}>Include tasks</span>
        <Select
          options={[
            {
              value: "yes",
              label: "Yes",
            },
            {
              value: "no",
              label: "No",
            },
          ]}
          onSelect={(_, optionRaw) => {
            const option = "yes" === optionRaw.value ? true : false;
            setIncludeTasksInProposalOutputAtMatterLevel(option);
          }}
          placeholder="Yes/No"
          defaultValue={includeTasksInProposalOutput === true ? "yes" : "no"}
          disabled={disableToggle}
        />
      </>
      {showHideRates && (
        <>
          <span className={proposalCustomisationSelectors}>Show Rates</span>
          <Select
            options={[
              {
                value: "yes",
                label: "Yes",
              },
              {
                value: "no",
                label: "No",
              },
            ]}
            onSelect={(_, optionRaw) => {
              const option = "yes" === optionRaw.value ? true : false;
              setIncludeRatesInProposalOutputAtMatterLevel(option);
            }}
            placeholder="Yes/No"
            defaultValue={includeRatesInProposalOutput === true ? "yes" : "no"}
          />
        </>
      )}
    </div>
  );
};

export const ParentProposalExportPreviewModal: FC<ProposalExportPreviewModalProps> = ({
  show,
  loading,
  logo,
  footer,
  downloading,
  proposals,
  onCancel,
  onDownload,
  fontFamily,
  brands,
  selectedBrand,
  selectBrand,
  includeTasksInProposalOutput,
  includeRatesInProposalOutput,
  includeFeeEarnersInProposalOutput,
  setIncludeRatesInProposalOutputAtMatterLevel,
  setIncludeTasksInProposalOutputAtMatterLevel,
  setIncludeFeeEarnersInProposalOutputAtMatterLevel,
  currencySymbol,
  taxLabel,
  disableTaskToggle,
  totals,
  excludeTax,
  taxLevelPercent,
  collated,
  onClose,
  numberedListFormat,
  lineBreakInLists,
}) => {
  const navigate = useNavigate();

  return (
    <Modal
      title={
        <ProposalPreviewTitle
          showHideRates={proposals?.some((x) => x.pricingLevel === PricingLevel.FeeEarner) ?? false}
          brands={brands}
          selectedBrand={selectedBrand}
          selectBrand={selectBrand}
          includeRatesInProposalOutput={includeRatesInProposalOutput}
          includeTasksInProposalOutput={includeTasksInProposalOutput}
          includeFeeEarnersInProposalOutput={includeRatesInProposalOutput}
          setIncludeRatesInProposalOutputAtMatterLevel={
            setIncludeRatesInProposalOutputAtMatterLevel
          }
          setIncludeTasksInProposalOutputAtMatterLevel={
            setIncludeTasksInProposalOutputAtMatterLevel
          }
          setIncludeFeeEarnersInProposalOutputAtMatterLevel={
            setIncludeFeeEarnersInProposalOutputAtMatterLevel
          }
          disableToggle={disableTaskToggle}
        />
      }
      open={show}
      width="60%"
      style={{ top: "36px" }}
      onCancel={onCancel}
      bodyStyle={{
        maxHeight: "75vh",
        overflowY: "scroll",
        backgroundColor: "gray",
      }}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Cancel
        </Button>,
        <Button
          key="download"
          type="primary"
          icon={<DownloadOutlined />}
          loading={downloading}
          disabled={downloading}
          onClick={onDownload}
        >
          Download
        </Button>,
      ]}
    >
      <div className={bodyContainerClassName} style={{ fontFamily }}>
        {loading ? (
          <Spin size="large" tip="Loading..." />
        ) : (
          (proposals ?? []).length > 0 &&
          proposals?.map((proposal) => {
            return (
              <div key={proposal.matterId}>
                <ProposalExportPreviewModalBody
                  showFeeEarners={includeFeeEarnersInProposalOutput}
                  numberedListFormat={numberedListFormat}
                  lineBreakInLists={lineBreakInLists}
                  showRates={includeRatesInProposalOutput}
                  includeTasksOverride={includeTasksInProposalOutput}
                  logo={logo}
                  footer={footer}
                  onClickEdit={(matterEditorChild: string) => {
                    onClose();
                    navigate(`/matter-editor/${proposal.matterId}/${matterEditorChild}`);
                  }}
                  proposal={proposal}
                  currencySymbol={currencySymbol}
                  taxLabel={taxLabel}
                />

                <Divider style={{ height: "20px" }}> End of {proposal.matterTitle} </Divider>
              </div>
            );
          })
        )}

        <table style={{ width: "100%" }}>
          <tbody>
            <Row style={{ marginTop: 10 }} label={"Overall Summary"} strong="label" />
            <Row
              label={`Fee subtotal${!excludeTax ? ` (excl. ${taxLabel})` : ""}`}
              values={
                collated?.fixedLegalFeeTotal.amount
                  ? [
                      <Text key="subTotalExcludingVat">{totals.subTotalExcludingVat}</Text>,
                      <Text key="fixedLegalFeeTotal">
                        {formatPrice(collated.fixedLegalFeeTotal, currencySymbol)}
                      </Text>,
                    ]
                  : [totals.subTotalExcludingVat]
              }
            />
            {!excludeTax && (
              <>
                <Row
                  label={`${taxLabel} ${formatPercentage(taxLevelPercent)}`}
                  values={
                    collated?.fixedLegalFeeTotal.amount
                      ? [
                          totals.vat,
                          formatPrice(collated.fixedLegalFeeTax.taxAmount, currencySymbol),
                        ]
                      : [totals.vat]
                  }
                />
                <SpacerRow />
                <Row
                  label={`Fee subtotal (including ${taxLabel})`}
                  values={
                    collated?.fixedLegalFeeTotal.amount
                      ? [
                          <Text key="subTotalIncludingVat">{totals.subTotalIncludingVat}</Text>,
                          <Text key="feeSubtotalExl">
                            {formatPrice(
                              collated.fixedLegalFeeTax.totalIncludingTax,
                              currencySymbol
                            )}
                          </Text>,
                        ]
                      : [totals.subTotalIncludingVat]
                  }
                />
              </>
            )}
            <Row
              label={`Disbursements${
                !excludeTax ? ` (including ${taxLabel} of ${totals.totalDisbursementsTax})` : ""
              }`}
              values={
                collated?.fixedLegalFeeTotal.amount
                  ? [totals.disbursementsTotal, totals.disbursementsTotal]
                  : [totals.disbursementsTotal]
              }
            />
            <SpacerRow />
            <Row
              label={"GRAND TOTAL"}
              values={
                collated?.fixedLegalFeeTotal.amount
                  ? [totals.grandTotal, formatPrice(collated.fixedGrandTotal, currencySymbol)]
                  : [totals.grandTotal]
              }
              strong="row"
            />
          </tbody>
        </table>
      </div>
    </Modal>
  );
};
